import { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, ShieldCheck, Sparkles, Crown } from "lucide-react";

const GRADE_LABELS = {
  farm_grade: { label: "Farm-Grade", color: "bg-[#22C55E]" },
  show_grade: { label: "Show-Grade", color: "bg-[#3B82F6]" },
  working_grade: { label: "Working-Grade", color: "bg-[#F59E0B]" },
  companion: { label: "Companion", color: "bg-[#8B5CF6]" },
};

const ListingCard = ({ listing }) => {
  const isPrivate = listing.is_private_listing;
  const [imgError, setImgError] = useState(false);
  
  const fallbackImage = "https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=400&h=300&fit=crop";
  const imgSrc = imgError ? fallbackImage : (listing.photos?.[0] || fallbackImage);
  
  const isStudOrMare = listing.listing_type === "stud_service" || listing.listing_type === "mare_service";
  const gradeInfo = listing.breeding_grade ? GRADE_LABELS[listing.breeding_grade] : null;
  const isRoyalty = listing.subscription_tier === "royalty";
  const isElite = listing.subscription_tier === "elite";

  return (
    <Link
      to={`/listing/${listing.listing_id}`}
      className={`group glass-card overflow-hidden transition-colors duration-300 block ${isRoyalty ? 'border-[#D4AF37]/50 hover:border-[#D4AF37]' : 'hover:border-[#D4AF37]/30'}`}
      data-testid={`listing-${listing.listing_id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#18181B]">
        <img
          src={imgSrc}
          alt={listing.title}
          className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${isPrivate ? "blur-md" : ""}`}
          loading="lazy"
          decoding="async"
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
          onError={() => setImgError(true)}
        />
        
        {/* Badges Container - Top Left */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {listing.in_season && (
            <span className="px-2 py-1 bg-[#22C55E] text-black text-xs font-medium rounded-full flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Available Now
            </span>
          )}
          {gradeInfo && (
            <span className={`px-2 py-1 ${gradeInfo.color} text-black text-xs font-medium rounded-full`}>
              {gradeInfo.label}
            </span>
          )}
          {isStudOrMare && (
            <span className="px-2 py-1 bg-[#D4AF37] text-black text-xs font-medium rounded-full">
              {listing.listing_type === "stud_service" ? "Stud" : "Mare"}
            </span>
          )}
        </div>
        
        {/* Premium Badge - Top Right */}
        {(isRoyalty || isElite) && (
          <div className="absolute top-2 right-2">
            <span className={`px-2 py-1 ${isRoyalty ? 'bg-gradient-to-r from-[#D4AF37] to-[#F5D77A]' : 'bg-[#8B5CF6]'} text-black text-xs font-medium rounded-full flex items-center gap-1`}>
              <Crown className="w-3 h-3" /> {isRoyalty ? 'Royalty' : 'Elite'}
            </span>
          </div>
        )}
        
        {isPrivate && (
          <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-black/50">
            <div className="text-center">
              <Lock className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
              <span className="text-sm text-[#D4AF37]">Private Listing</span>
            </div>
          </div>
        )}
      </div>
      <div className="p-4">
        <h4 className="text-lg text-[#EDEDED] mb-1 truncate">
          {listing.animal_name || listing.title}
        </h4>
        <p className="text-[#D4AF37] font-medium mb-2">
          {isPrivate ? "Contact for Price" : isStudOrMare ? `$${listing.price?.toLocaleString()} service fee` : `$${listing.price?.toLocaleString()}`}
        </p>
        <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
          <span>{listing.breed}</span>
          <span>•</span>
          <span>
            {listing.location_city}, {listing.location_state}
          </span>
        </div>
        {listing.breeding_method && (
          <div className="mt-2 text-xs text-[#71717A]">
            {listing.breeding_method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </div>
        )}
        {listing.registry_organization && (
          <div className="mt-2 flex items-center gap-1">
            <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs text-[#A1A1AA]">{listing.registry_organization}</span>
          </div>
        )}
        {listing.verified_documents && (
          <div className="mt-1 flex items-center gap-1">
            <ShieldCheck className="w-4 h-4 text-[#22C55E]" />
            <span className="text-xs text-[#22C55E]">Health Docs Verified</span>
          </div>
        )}
      </div>
    </Link>
  );
};

export default ListingCard;
